import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import {
  DoorOpen, Users, Search, Edit, Sparkles, Loader2,
  Bed, IndianRupee, Building2, Wrench, CheckCircle2, XCircle, ChevronRight, Plus, Camera
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import AddRoomModal from './AddRoomModal';
import BulkAddRoomsModal from './BulkAddRoomsModal';
import RoomPhotosModal from './RoomPhotosModal';
import axios from 'axios';
import { emit, EVT } from '../../services/dataSyncBus';
import { useDataSync } from '../../hooks/useDataSync';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  // Auto-derived from active tenant count
  fully_occupied:     { label: 'Occupied',        cls: 'bg-green-100 text-green-800' },
  partially_occupied: { label: 'Partly Occupied', cls: 'bg-green-50 text-green-700' },
  occupied:           { label: 'Occupied',        cls: 'bg-green-100 text-green-800' },
  notice:             { label: 'Notice Period',   cls: 'bg-yellow-100 text-yellow-800' },
  // No tenants
  available:          { label: 'Available',       cls: 'bg-blue-100 text-blue-800' },
  vacant:             { label: 'Available',       cls: 'bg-blue-100 text-blue-800' },
  // Manual statuses (preserved only when no tenants)
  booked:             { label: 'Booked',          cls: 'bg-purple-100 text-purple-800' },
  under_maintenance:  { label: 'Maintenance',     cls: 'bg-red-100 text-red-800' },
  maintenance:        { label: 'Maintenance',     cls: 'bg-red-100 text-red-800' },
};

const RoomsList = () => {
  const { token } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [propertyFilter, setPropertyFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [editFormData, setEditFormData] = useState({
    room_number: '', rent_amount: '', deposit_amount: '', total_beds: '', status: 'vacant',
  });
  const [predictingId, setPredictingId] = useState(null);
  const [prediction, setPrediction] = useState(null); // { room, data }
  const [showAddRoom, setShowAddRoom] = useState(false);
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const [photoRoom, setPhotoRoom] = useState(null); // room for photo modal
  const [addRoomPropertyId, setAddRoomPropertyId] = useState(''); // selected property for Add Room

  const fetchData = useCallback(async () => {
    try {
      const [propertiesRes, tenantsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/properties`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/tenants`,    { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/bookings`,   { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
      ]);
      const activeBookings = (bookingsRes.data || []).filter(b => ['pending', 'confirmed'].includes(b.status));
      const allRooms = [];
      for (const property of propertiesRes.data) {
        const roomsRes = await axios.get(`${API}/properties/${property.id}/rooms`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        roomsRes.data.forEach(room => {
          const roomTenants = tenantsRes.data.filter(t => t.room_id === room.id && t.property_id === property.id && t.status === 'active');
          // Active booking on this room (pending/confirmed) — surfaces holder name + rent
          const activeBooking = activeBookings.find(b => b.room_id === room.id) || null;
          allRooms.push({
            ...room,
            property_name: property.name,
            property_city: property.city,
            property_id: property.id,
            tenants: roomTenants,
            activeBooking,
          });
        });
      }
      setProperties(propertiesRes.data);
      setRooms(allRooms);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh on tenant lifecycle, payment, room or booking mutations anywhere
  useDataSync([EVT.ROOM, EVT.TENANT, EVT.BOOKING, EVT.PAYMENT], fetchData);

  const handleEdit = (room) => {
    setSelectedRoom(room);
    setEditFormData({
      room_number: room.room_number || '',
      rent_amount: (room.rent_per_bed ?? room.rent_amount ?? '').toString(),
      deposit_amount: room.deposit_amount?.toString() || '',
      total_beds: room.total_beds?.toString() || '',
      status: room.status || 'vacant',
    });
    setShowEditModal(true);
  };

  const handleUpdateRoom = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API}/properties/${selectedRoom.property_id}/rooms/${selectedRoom.id}`,
        {
          ...editFormData,
          rent_per_bed: parseFloat(editFormData.rent_amount),
          deposit_amount: parseFloat(editFormData.deposit_amount),
          total_beds: parseInt(editFormData.total_beds),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      emit(EVT.ROOM, { action: 'updated', id: selectedRoom.id });
      toast({ title: "Room Updated!", description: "Room details have been updated successfully" });
      setShowEditModal(false);
      setSelectedRoom(null);
      fetchData();
    } catch (error) {
      let errorMessage = "Failed to update room";
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') errorMessage = detail;
        else if (Array.isArray(detail)) errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        else if (typeof detail === 'object') errorMessage = JSON.stringify(detail);
      }
      toast({ title: "Error", description: errorMessage, variant: "destructive" });
    }
  };

  const handlePredictRent = async (room) => {
    setPredictingId(room.id);
    setPrediction(null);
    try {
      const payload = {
        property_data: {
          property_name: room.property_name,
          city: room.property_city,
          room_number: room.room_number,
          total_beds: room.total_beds || 1,
          room_type: room.room_type || (room.total_beds > 1 ? 'sharing' : 'private'),
          current_rent: room.rent_per_bed ?? room.rent_amount ?? 0,
          amenities: room.amenities || [],
        }
      };
      const res = await axios.post(`${API}/ai/predict/rent`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPrediction({ room, data: res.data });
    } catch (e) {
      toast({ title: 'AI Prediction failed', description: e.response?.data?.detail || 'Try again later', variant: 'destructive' });
    } finally { setPredictingId(null); }
  };

  // Helper: is this room currently holding a tenant (any of the auto-derived "occupied" statuses)
  const isOccupiedStatus = (s) => s === 'occupied' || s === 'fully_occupied' || s === 'partially_occupied';

  const filteredRooms = useMemo(() => rooms.filter(room => {
    const s = searchTerm.toLowerCase();
    const matchesSearch = !s
      || room.room_number?.toLowerCase().includes(s)
      || room.property_name?.toLowerCase().includes(s)
      || room.tenants?.some(t => t.name?.toLowerCase().includes(s));
    const matchesProperty = propertyFilter === 'all' || room.property_id === propertyFilter;
    // "occupied" filter matches all 3 occupied variants
    const matchesStatus =
      statusFilter === 'all'
        ? true
        : statusFilter === 'occupied'
          ? isOccupiedStatus(room.status)
          : room.status === statusFilter;
    return matchesSearch && matchesProperty && matchesStatus;
  }), [rooms, searchTerm, propertyFilter, statusFilter]);

  const stats = useMemo(() => ({
    total:        rooms.length,
    occupied:     rooms.filter(r => isOccupiedStatus(r.status)).length,
    vacant:       rooms.filter(r => r.status === 'vacant' || r.status === 'available').length,
    booked:       rooms.filter(r => r.status === 'booked').length,
    notice:       rooms.filter(r => r.status === 'notice').length,
    maintenance:  rooms.filter(r => r.status === 'under_maintenance' || r.status === 'maintenance').length,
    totalBeds:    rooms.reduce((sum, r) => sum + (r.total_beds || 0), 0),
    occupiedBeds: rooms.reduce((sum, r) => sum + (r.occupied_beds || 0), 0),
    totalRent:    rooms.reduce((sum, r) => sum + (((r.rent_per_bed ?? r.rent_amount) || 0) * (r.occupied_beds || 0)), 0),
  }), [rooms]);

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading rooms...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="rooms-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">All Rooms</h1>
          <p className="text-gray-600 mt-1">Inventory across all properties — beds, rent, occupancy & AI rent suggestions</p>
        </div>
        <div className="flex items-center gap-2">
          {properties.length > 0 ? (
            <>
              <select
                data-testid="add-room-property-picker"
                value={addRoomPropertyId}
                onChange={(e) => setAddRoomPropertyId(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
              >
                <option value="">Select property…</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <Button
                data-testid="bulk-add-room-btn"
                disabled={!addRoomPropertyId}
                onClick={() => setShowBulkAdd(true)}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:opacity-90 disabled:opacity-40"
              >
                <Plus size={16} className="mr-1" /> Bulk Add Rooms
              </Button>
              <Button
                data-testid="add-room-btn"
                disabled={!addRoomPropertyId}
                onClick={() => setShowAddRoom(true)}
                variant="outline"
                className="disabled:opacity-40"
                title="Add a single room with custom details"
              >
                + 1
              </Button>
            </>
          ) : (
            <Button
              data-testid="add-room-empty-cta"
              onClick={() => window.location.href = '/manager/property/list'}
              variant="outline"
              className="border-blue-300 text-blue-600 hover:bg-blue-50"
            >
              <Plus size={16} className="mr-1" /> Add Property First
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card className="p-4 border-l-4 border-l-blue-500" data-testid="room-stat-total">
          <p className="text-[11px] text-gray-500 uppercase">Total Rooms</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</p>
        </Card>
        <Card className="p-4 border-l-4 border-l-purple-500">
          <p className="text-[11px] text-gray-500 uppercase">Total Beds</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalBeds}</p>
        </Card>
        <Card className="p-4 border-l-4 border-l-green-500">
          <p className="text-[11px] text-gray-500 uppercase">Occupied Beds</p>
          <p className="text-2xl font-bold text-green-700 mt-1">{stats.occupiedBeds}</p>
        </Card>
        <Card className="p-4 border-l-4 border-l-orange-500">
          <p className="text-[11px] text-gray-500 uppercase">Vacant Rooms</p>
          <p className="text-2xl font-bold text-orange-700 mt-1">{stats.vacant}</p>
        </Card>
        <Card className="p-4 border-l-4 border-l-red-500">
          <p className="text-[11px] text-gray-500 uppercase">Maintenance</p>
          <p className="text-2xl font-bold text-red-700 mt-1">{stats.maintenance}</p>
        </Card>
        <Card className="p-4 border-l-4 border-l-emerald-500">
          <p className="text-[11px] text-gray-500 uppercase">Monthly Rent</p>
          <p className="text-xl font-bold text-emerald-700 mt-1">₹{stats.totalRent.toLocaleString()}</p>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <Input
              data-testid="rooms-search"
              placeholder="Search room number, property, tenant..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            data-testid="rooms-property-filter"
            value={propertyFilter}
            onChange={(e) => setPropertyFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">All Properties</option>
            {properties.map(prop => <option key={prop.id} value={prop.id}>{prop.name}</option>)}
          </select>
          <select
            data-testid="rooms-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">All Status ({stats.total})</option>
            <option value="occupied">Occupied ({stats.occupied})</option>
            <option value="vacant">Vacant ({stats.vacant})</option>
            <option value="booked">Booked ({stats.booked})</option>
            <option value="notice">Notice ({stats.notice})</option>
            <option value="under_maintenance">Maintenance ({stats.maintenance})</option>
          </select>
        </div>
      </Card>

      {/* DENSE TABLE */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="rooms-table">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Room</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Property</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Beds</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Rent</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Deposit</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase min-w-[180px]">Tenants</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredRooms.length === 0 ? (
                <tr><td colSpan="8" className="text-center py-12 text-gray-500" data-testid="rooms-empty">
                  <DoorOpen size={40} className="mx-auto text-gray-300 mb-2" />
                  {properties.length === 0 ? (
                    <>
                      <p className="font-medium text-gray-700 mb-1">No properties yet</p>
                      <p className="text-xs mb-4">Rooms live inside properties. Add a property first, then you can add rooms.</p>
                      <Button
                        onClick={() => window.location.href = '/manager/property/list'}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="rooms-empty-add-property"
                      >
                        <Plus size={14} className="mr-1" /> Add Property
                      </Button>
                    </>
                  ) : rooms.length === 0 ? (
                    <>
                      <p className="font-medium text-gray-700 mb-1">No rooms yet</p>
                      <p className="text-xs">Pick a property above and click <strong>Add Room</strong> to create your first room.</p>
                    </>
                  ) : (
                    <p>No rooms match the current filters.</p>
                  )}
                </td></tr>
              ) : filteredRooms.map(room => {
                const status = STATUS_META[room.status] || STATUS_META.vacant;
                const totalB = room.total_beds || 0;
                const occB   = room.occupied_beds || 0;
                const bedPct = totalB > 0 ? Math.round((occB / totalB) * 100) : 0;
                return (
                  <tr key={`${room.property_id}-${room.id}`} className="hover:bg-blue-50/40" data-testid={`room-row-${room.id}`}>
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-center gap-2">
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-white ${
                          room.status === 'occupied' ? 'bg-green-500' :
                          room.status === 'under_maintenance' || room.status === 'maintenance' ? 'bg-red-500' :
                          room.status === 'booked' ? 'bg-purple-500' :
                          'bg-orange-500'
                        }`}>
                          <DoorOpen size={16} />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">Room {room.room_number}</p>
                          <p className="text-[11px] text-gray-500">{room.room_type || (totalB > 1 ? `${totalB}-Sharing` : 'Private')}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-start gap-1 text-gray-700">
                        <Building2 size={12} className="mt-1 text-gray-400" />
                        <div>
                          <p className="font-medium truncate max-w-[160px]">{room.property_name}</p>
                          <p className="text-[11px] text-gray-500">{room.property_city}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-center gap-1">
                        <Bed size={12} className="text-purple-500" />
                        <span className="font-medium text-gray-900">{occB}/{totalB}</span>
                      </div>
                      <div className="w-16 h-1 bg-gray-100 rounded-full mt-1 overflow-hidden">
                        <div className={`h-full ${bedPct >= 80 ? 'bg-green-500' : bedPct >= 40 ? 'bg-yellow-500' : 'bg-orange-400'}`} style={{ width: `${bedPct}%` }}></div>
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <span className="inline-flex items-center gap-0.5 text-green-700 font-semibold">
                        <IndianRupee size={12} />{((room.rent_per_bed ?? room.rent_amount) || 0).toLocaleString()}
                      </span>
                      {(room.photos?.length ?? 0) > 0 && (
                        <div className="flex -space-x-1 mt-1.5">
                          {room.photos.slice(0, 3).map((src, i) => (
                            <img
                              key={`${room.id}-photo-${src}-${i}`}
                              src={src}
                              alt=""
                              onClick={() => setPhotoRoom(room)}
                              className="w-7 h-7 rounded border-2 border-white shadow-sm object-cover cursor-pointer hover:scale-110 transition-transform"
                            />
                          ))}
                          {room.photos.length > 3 && (
                            <div className="w-7 h-7 rounded border-2 border-white shadow-sm bg-gray-600 text-white flex items-center justify-center text-[9px] font-bold">
                              +{room.photos.length - 3}
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top text-gray-700">
                      <span className="inline-flex items-center gap-0.5 text-xs">
                        <IndianRupee size={10} />{(room.deposit_amount || 0).toLocaleString()}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top min-w-[220px]">
                      {room.tenants && room.tenants.length > 0 ? (
                        <div className="space-y-1.5" data-testid={`room-tenants-${room.id}`}>
                          {room.tenants.slice(0, 3).map(t => (
                            <div key={t.id} className="text-xs">
                              <div className="flex items-center gap-1.5">
                                <div className="w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px] font-bold">
                                  {t.name?.charAt(0)}
                                </div>
                                <span className="font-semibold text-gray-900 truncate max-w-[110px]">{t.name}</span>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${t.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                  {t.payment_status === 'paid' ? '✓ Paid' : '! Due'}
                                </span>
                              </div>
                              <div className="ml-7 text-[10px] text-gray-500 leading-snug">
                                ₹{(t.rent_amount || 0).toLocaleString()}/mo · Dep ₹{(t.deposit_amount || 0).toLocaleString()}
                                {t.check_in_date && <> · since {new Date(t.check_in_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</>}
                              </div>
                            </div>
                          ))}
                          {room.tenants.length > 3 && (
                            <p className="text-[11px] text-gray-500">+{room.tenants.length - 3} more roommate{room.tenants.length - 3 > 1 ? 's' : ''}</p>
                          )}
                        </div>
                      ) : room.activeBooking ? (
                        <div className="text-xs space-y-1" data-testid={`room-booking-${room.id}`}>
                          <div className="flex items-center gap-1.5">
                            <div className="w-5 h-5 rounded-full bg-purple-600 text-white flex items-center justify-center text-[10px] font-bold">
                              {room.activeBooking.name?.charAt(0)}
                            </div>
                            <span className="font-semibold text-gray-900 truncate max-w-[110px]">{room.activeBooking.name}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                              room.activeBooking.status === 'confirmed' ? 'bg-purple-100 text-purple-800' : 'bg-amber-100 text-amber-800'
                            }`}>
                              {room.activeBooking.status === 'confirmed' ? 'BOOKED' : 'PENDING'}
                            </span>
                          </div>
                          <div className="ml-7 text-[10px] text-gray-500 leading-snug">
                            ₹{(room.activeBooking.rent_amount || 0).toLocaleString()}/mo · Dep ₹{(room.activeBooking.deposit_amount || 0).toLocaleString()}
                            {room.activeBooking.check_in_date && <> · in {new Date(room.activeBooking.check_in_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</>}
                          </div>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">— Vacant —</span>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <span className={`text-[11px] px-2 py-1 rounded-full font-medium ${status.cls}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 align-top text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          data-testid={`room-photos-${room.id}`}
                          onClick={() => setPhotoRoom(room)}
                          className={`relative p-2 rounded ${(room.photos?.length ?? 0) > 0 ? 'text-blue-600 bg-blue-50 hover:bg-blue-100' : 'text-gray-500 hover:bg-gray-100'}`}
                          title={`${room.photos?.length ?? 0} photo${(room.photos?.length ?? 0) === 1 ? '' : 's'}`}
                        >
                          <Camera size={14} />
                          {(room.photos?.length ?? 0) > 0 && (
                            <span className="absolute -top-1 -right-1 text-[9px] font-bold bg-blue-600 text-white rounded-full w-4 h-4 flex items-center justify-center">
                              {room.photos.length}
                            </span>
                          )}
                        </button>
                        <button
                          data-testid={`room-predict-${room.id}`}
                          onClick={() => handlePredictRent(room)}
                          disabled={predictingId === room.id}
                          className="p-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded hover:opacity-90 disabled:opacity-50"
                          title="AI Rent Prediction"
                        >
                          {predictingId === room.id ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                        </button>
                        <button
                          data-testid={`room-edit-${room.id}`}
                          onClick={() => handleEdit(room)}
                          className="p-2 text-gray-600 hover:bg-gray-100 rounded"
                          title="Edit Room"
                        >
                          <Edit size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* AI Prediction Panel */}
      <Dialog open={!!prediction} onOpenChange={() => setPrediction(null)}>
        <DialogContent className="max-w-lg" data-testid="ai-rent-prediction-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="text-purple-600" size={20} /> AI Rent Prediction
            </DialogTitle>
          </DialogHeader>
          {prediction && (
            <div className="space-y-4">
              <div className="text-sm text-gray-600">
                Room <strong>{prediction.room.room_number}</strong> at <strong>{prediction.room.property_name}</strong>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                  <p className="text-[10px] text-green-700 uppercase font-semibold">Suggested</p>
                  <p className="text-xl font-bold text-green-800 mt-1">₹{(prediction.data.suggested_rent || 0).toLocaleString()}</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                  <p className="text-[10px] text-blue-700 uppercase font-semibold">Min</p>
                  <p className="text-xl font-bold text-blue-800 mt-1">₹{(prediction.data.min_rent || 0).toLocaleString()}</p>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
                  <p className="text-[10px] text-orange-700 uppercase font-semibold">Max</p>
                  <p className="text-xl font-bold text-orange-800 mt-1">₹{(prediction.data.max_rent || 0).toLocaleString()}</p>
                </div>
              </div>
              {prediction.data.market_average != null && (
                <div className="flex items-center justify-between text-sm bg-gray-50 rounded-lg p-3">
                  <span className="text-gray-600">Market Average</span>
                  <span className="font-semibold">₹{(prediction.data.market_average || 0).toLocaleString()}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-sm bg-gray-50 rounded-lg p-3">
                <span className="text-gray-600">Current Rent</span>
                <span className="font-semibold">₹{((prediction.room.rent_per_bed ?? prediction.room.rent_amount) || 0).toLocaleString()}</span>
              </div>
              {prediction.data.factors && prediction.data.factors.length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 uppercase mb-2">Key factors</p>
                  <ul className="space-y-1">
                    {prediction.data.factors.map((f, i) => (
                      <li key={`factor-${f}-${i}`} className="text-xs text-gray-700 flex items-start gap-1">
                        <ChevronRight size={12} className="mt-0.5 text-purple-500" />{f}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {prediction.data.reasoning && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-xs text-purple-900 italic">
                  {prediction.data.reasoning}
                </div>
              )}
              <Button
                className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white"
                onClick={() => {
                  setEditFormData({
                    room_number: prediction.room.room_number,
                    rent_amount: String(prediction.data.suggested_rent || prediction.room.rent_per_bed || prediction.room.rent_amount),
                    deposit_amount: String(prediction.room.deposit_amount || 0),
                    total_beds: String(prediction.room.total_beds || 1),
                    status: prediction.room.status || 'vacant',
                  });
                  setSelectedRoom(prediction.room);
                  setPrediction(null);
                  setShowEditModal(true);
                }}
                data-testid="apply-ai-rent-btn"
              >
                Apply Suggested Rent →
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Room Modal */}
      {addRoomPropertyId && (
        <AddRoomModal
          isOpen={showAddRoom}
          onClose={() => setShowAddRoom(false)}
          onSuccess={() => { setShowAddRoom(false); fetchData(); }}
          propertyId={addRoomPropertyId}
          token={token}
        />
      )}

      {/* Bulk Add Rooms Modal */}
      {addRoomPropertyId && (
        <BulkAddRoomsModal
          isOpen={showBulkAdd}
          onClose={() => setShowBulkAdd(false)}
          onSuccess={() => { setShowBulkAdd(false); fetchData(); }}
          propertyId={addRoomPropertyId}
          propertyName={properties.find(p => p.id === addRoomPropertyId)?.name || 'this property'}
          token={token}
        />
      )}

      {/* Room Photos Modal */}
      {photoRoom && (
        <RoomPhotosModal
          isOpen={!!photoRoom}
          onClose={() => setPhotoRoom(null)}
          room={photoRoom}
          propertyId={photoRoom.property_id}
          token={token}
          onUpdated={(newPhotos) => {
            setRooms(prev => prev.map(r => r.id === photoRoom.id ? { ...r, photos: newPhotos } : r));
            setPhotoRoom(prev => prev ? { ...prev, photos: newPhotos } : prev);
          }}
        />
      )}

      {/* Edit Room Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Room {selectedRoom?.room_number}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdateRoom} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Room Number *</Label>
                <Input value={editFormData.room_number} onChange={(e) => setEditFormData({...editFormData, room_number: e.target.value})} required />
              </div>
              <div>
                <Label>Total Beds *</Label>
                <Input type="number" value={editFormData.total_beds} onChange={(e) => setEditFormData({...editFormData, total_beds: e.target.value})} required />
              </div>
              <div>
                <Label>Rent Amount (₹) *</Label>
                <Input type="number" value={editFormData.rent_amount} onChange={(e) => setEditFormData({...editFormData, rent_amount: e.target.value})} required />
              </div>
              <div>
                <Label>Deposit Amount (₹) *</Label>
                <Input type="number" value={editFormData.deposit_amount} onChange={(e) => setEditFormData({...editFormData, deposit_amount: e.target.value})} required />
              </div>
              <div className="col-span-2">
                <Label>Status</Label>
                {(selectedRoom?.tenants?.length || 0) > 0 ? (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
                    <strong>Auto-managed:</strong> this room has {selectedRoom?.tenants?.length} active tenant(s),
                    so its status is derived automatically (occupied / partially occupied / notice).
                    To change it, exit the tenant(s) first.
                  </div>
                ) : (
                  <select
                    value={editFormData.status}
                    onChange={(e) => setEditFormData({...editFormData, status: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    data-testid="room-status-select"
                  >
                    <option value="available">Available</option>
                    <option value="booked">Booked (locked for booking)</option>
                    <option value="under_maintenance">Under Maintenance</option>
                  </select>
                )}
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">Update Room</Button>
              <Button type="button" onClick={() => setShowEditModal(false)} variant="outline" className="flex-1">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoomsList;
